// frontend/src/components/Chart/CompactMonitoringChart.jsx
//
// Compact monitoring strip above the main chart. Three panes answering three
// distinct questions — MA stack (which way is the trend), Williams %R (is now a
// pullback/entry moment), OBV (does volume confirm the trend, incl. divergence)
// — plus DCA. (Seasonal tendency moved to its own left-rail component,
// SeasonalTendency.jsx.)
import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import DCAPane from './DCAPane';
import InfoHint from '../Common/InfoHint';
import WatchButton from './WatchButton';

const PANE_POINTS = 50; // bars shown in the mini line charts

const CompactMonitoringChart = ({
  candleData,
  height = 300,
  symbol,
  isAuthed = true,
  onRequestLogin = () => {},
}) => {
  // ---- Indicator panes ----
  const { maData, rsiData, obvData, rsiValue, divergence, maStack, evidence } = useMemo(() => {
    if (!candleData || candleData.length < 200) {
      return { maData: [], rsiData: [], obvData: [], rsiValue: null, divergence: { type: 'NONE' }, maStack: null, evidence: null };
    }

    const closes = candleData.map(c => c.close);
    const ma20 = sma(closes, 20);
    const ma50 = sma(closes, 50);
    const ma200 = sma(closes, 200);
    const rsiArr = rsiSeries(closes, 14);
    const obv = onBalanceVolume(candleData);

    const start = Math.max(0, closes.length - PANE_POINTS);
    const slice = (fn) => candleData.slice(start).map((c, i) => fn(start + i, c));

    const maData = slice((idx, c) => ({
      time: c.time, price: c.close, ma20: ma20[idx], ma50: ma50[idx], ma200: ma200[idx],
    }));
    const rsiData = slice((idx, c) => ({ time: c.time, rsi: rsiArr[idx] }));
    const obvData = slice((idx, c) => ({ time: c.time, obv: obv[idx] }));

    const last = closes.length - 1;
    let maStack = 'MIXED';
    if (closes[last] > ma50[last] && ma50[last] > ma200[last]) maStack = 'BULLISH';
    else if (closes[last] < ma50[last] && ma50[last] < ma200[last]) maStack = 'BEARISH';

    const div = detectObvDivergence(closes, obv);

    // ---- Rule evidence: trade each indicator's textbook buy/sell rule over the
    // whole history and report the round-trip win rate — "does this stock
    // actually follow this indicator?" (pure price follow-through, no fees) ----
    const n = closes.length;
    const rsiNow = rsiArr[last];

    // RSI rule (validated: the strongest simple rule on quiet blue chips):
    // buy when RSI(14) crosses DOWN through 30, sell when it crosses UP through 70
    const rsiBuy = new Array(n).fill(false);
    const rsiSell = new Array(n).fill(false);
    for (let i = 1; i < n; i++) {
      if (rsiArr[i - 1] !== null && rsiArr[i] !== null) {
        rsiBuy[i] = rsiArr[i - 1] >= 30 && rsiArr[i] < 30;
        rsiSell[i] = rsiArr[i - 1] <= 70 && rsiArr[i] > 70;
      }
    }

    // MA stack rule: buy when the bullish stack forms, sell when close breaks MA50
    const stackTrue = (i) => ma200[i] !== null && closes[i] > ma50[i] && ma50[i] > ma200[i];
    const maBuy = new Array(n).fill(false);
    const maSell = new Array(n).fill(false);
    for (let i = 1; i < n; i++) {
      if (ma200[i] === null || ma200[i - 1] === null) continue;
      maBuy[i] = stackTrue(i) && !stackTrue(i - 1);
      maSell[i] = closes[i] < ma50[i] && closes[i - 1] >= ma50[i - 1];
    }

    // OBV rule: buy on confirmed bullish divergence, sell on confirmed bearish
    const events = allDivergenceEvents(closes, obv);
    const obvBuy = new Array(n).fill(false);
    const obvSell = new Array(n).fill(false);
    for (const e of events) {
      const confirmBar = e.at + 3; // pivot only knowable 3 bars later
      if (confirmBar < n) {
        if (e.type === 'BULLISH') obvBuy[confirmBar] = true;
        else obvSell[confirmBar] = true;
      }
    }

    return {
      maData, rsiData, obvData,
      rsiValue: rsiNow,
      divergence: div,
      maStack,
      evidence: {
        rsi: ruleWinRate(rsiBuy, rsiSell, closes),
        ma: ruleWinRate(maBuy, maSell, closes),
        obv: ruleWinRate(obvBuy, obvSell, closes),
      },
    };
  }, [candleData]);

  const priceQuantile = useMemo(() => {
    if (!candleData || candleData.length === 0) return null;
    const recent = candleData.slice(-Math.min(2000, candleData.length));
    const currentPrice = recent[recent.length - 1]?.close;
    if (typeof currentPrice !== 'number') return null;
    const values = recent.map(c => c.close).filter(v => typeof v === 'number');
    if (values.length < 2) return null;
    const below = values.filter(v => v <= currentPrice).length;
    return Math.round((below / values.length) * 100);
  }, [candleData]);

  const drawdownRunup = useMemo(() => {
    if (!candleData || candleData.length === 0) return null;
    const recent = candleData.slice(-Math.min(200, candleData.length));
    const currentPrice = recent[recent.length - 1]?.close;
    if (typeof currentPrice !== 'number') return null;
    const values = recent.map(c => c.close).filter(v => typeof v === 'number');
    if (values.length < 2) return null;
    const maxPrice = Math.max(...values);
    const minPrice = Math.min(...values);
    if (maxPrice <= 0 || minPrice <= 0) return null;
    return {
      drawdownPct: ((currentPrice - maxPrice) / maxPrice) * 100,
      runupPct: ((currentPrice - minPrice) / minPrice) * 100,
    };
  }, [candleData]);

  const latestCandleLabel = useMemo(() => {
    if (!candleData || candleData.length === 0) return 'N/A';
    const last = candleData[candleData.length - 1];
    const ts = last.timestamp || last.time;
    if (!ts) return 'N/A';
    const ms = typeof ts === 'number' ? (ts > 1000000000000 ? ts : ts * 1000) : new Date(ts).getTime();
    if (!Number.isFinite(ms)) return 'N/A';
    return new Date(ms).toLocaleString();
  }, [candleData]);

  const tooltipStyle = {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    color: '#111827',
    fontSize: '12px',
  };

  const formatTime = (time) => {
    const ms = typeof time === 'number' ? (time > 1000000000000 ? time : time * 1000) : new Date(time).getTime();
    return Number.isFinite(ms) ? new Date(ms).toLocaleDateString() : time;
  };

  if (!candleData || candleData.length < 50) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4" style={{ height }}>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500 dark:text-gray-400">Insufficient data for monitoring chart</p>
        </div>
      </div>
    );
  }

  const rsiZone = rsiValue === null ? null
    : rsiValue > 70 ? { text: 'overbought', color: 'text-red-600 dark:text-red-400' }
    : rsiValue < 30 ? { text: 'oversold', color: 'text-green-600 dark:text-green-400' }
    : { text: 'neutral', color: 'text-gray-500 dark:text-gray-400' };

  // Keep badge text short: on phone the cards are ~150px wide and the badge is
  // whitespace-nowrap (for header alignment), so long text overflows/clips.
  const divergenceBadge = divergence.type === 'BULLISH'
    ? { text: 'bull. div', cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' }
    : divergence.type === 'BEARISH'
      ? { text: 'bear. div', cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
      : { text: 'confirmed', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };

  const maBadge = maStack === 'BULLISH'
    ? { text: 'bullish', cls: 'text-green-600 dark:text-green-400' }
    : maStack === 'BEARISH'
      ? { text: 'bearish', cls: 'text-red-600 dark:text-red-400' }
      : { text: 'mixed', cls: 'text-gray-500 dark:text-gray-400' };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">{symbol}</h3>
          <WatchButton symbol={symbol} isAuthed={isAuthed} onRequestLogin={onRequestLogin} />
          {priceQuantile !== null && (
            <span className="text-xs font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Q{priceQuantile}
            </span>
          )}
          {drawdownRunup && (
            <>
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                DD {drawdownRunup.drawdownPct.toFixed(1)}%
              </span>
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                RU +{drawdownRunup.runupPct.toFixed(1)}%
              </span>
            </>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{latestCandleLabel}</span>
      </div>

      {/* Trend / Timing / Confirmation / DCA panes — responsive: 1 col on
          phone, 2 on tablet, 4 on laptop+ */}
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4 sm:gap-4 mt-3">
        {/* MA — trend */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
          <div className="flex items-start justify-between gap-2 mb-1 min-h-[2.5rem]">
            <span className="flex items-start gap-1"><h4 className="text-sm font-medium leading-tight text-gray-700 dark:text-gray-300">Trend (MA 20/50/200)</h4><InfoHint title="Trend (moving averages)" align="left">Price with its 20, 50 and 200-bar moving averages. When price sits above MA50 and MA50 above MA200 ("bullish stack") the trend is up; the reverse is down. The tag below shows how buying that stack historically worked on this stock.</InfoHint></span>
            <span className={`text-xs font-medium shrink-0 whitespace-nowrap ${maBadge.cls}`}>{maBadge.text}</span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={maData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="time" tick={false} />
              <YAxis domain={['auto', 'auto']} width={45} tick={{ fontSize: 10 }} stroke="#6B7280" />
              <Tooltip
                formatter={(v, name) => [typeof v === 'number' ? v.toFixed(2) : v, name]}
                labelFormatter={(t) => formatTime(t)}
                contentStyle={tooltipStyle}
              />
              <Line type="monotone" dataKey="price" stroke="#111827" strokeWidth={2} dot={false} name="Price" />
              <Line type="monotone" dataKey="ma20" stroke="#2962FF" strokeWidth={1.5} dot={false} name="MA20" />
              <Line type="monotone" dataKey="ma50" stroke="#FF6B35" strokeWidth={1.5} dot={false} name="MA50" />
              <Line type="monotone" dataKey="ma200" stroke="#9C27B0" strokeWidth={1.5} dot={false} name="MA200" />
            </LineChart>
          </ResponsiveContainer>
          <RuleTag
            name="MA Stack Trend"
            description="Buy when an uptrend structure forms (price above MA50, MA50 above MA200). Sell when price closes back below MA50. Trend-following: ride the stack while it lasts."
            stat={evidence?.ma}
            locked={!isAuthed}
            onRequestLogin={onRequestLogin}
          />
        </div>

        {/* RSI — panic / euphoria mean reversion */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
          <div className="flex items-start justify-between gap-2 mb-1 min-h-[2.5rem]">
            <span className="flex items-start gap-1"><h4 className="text-sm font-medium leading-tight text-gray-700 dark:text-gray-300">RSI (14)</h4><InfoHint title="RSI — momentum" align="left">Momentum on a 0–100 scale. Above 70 = overbought (pullback risk), below 30 = oversold (panic — historically a buy zone on range-bound stocks). The tag below backtests that exact buy-fear / sell-greed rule here.</InfoHint></span>
            {rsiZone && <span className={`text-xs font-medium shrink-0 whitespace-nowrap ${rsiZone.color}`}>{rsiZone.text}</span>}
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={rsiData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="time" tick={false} />
              <YAxis domain={[0, 100]} width={45} tick={{ fontSize: 10 }} stroke="#6B7280" />
              <Tooltip
                formatter={(v) => [typeof v === 'number' ? v.toFixed(1) : v, 'RSI']}
                labelFormatter={(t) => formatTime(t)}
                contentStyle={tooltipStyle}
              />
              <ReferenceLine y={70} stroke="#EF4444" strokeDasharray="2 2" strokeWidth={2} />
              <ReferenceLine y={30} stroke="#10B981" strokeDasharray="2 2" strokeWidth={2} />
              <ReferenceLine y={50} stroke="#6B7280" strokeDasharray="1 1" strokeWidth={1} />
              <Line type="monotone" dataKey="rsi" stroke="#3B82F6" strokeWidth={2} dot={false} name="RSI" />
            </LineChart>
          </ResponsiveContainer>
          <RuleTag
            name="RSI Reversion"
            description="Buy when RSI(14) drops below 30 (panic, oversold). Sell when RSI rises above 70 (euphoria, overbought). Buy fear, sell greed — rare signals, works best on range-bound blue chips."
            stat={evidence?.rsi}
            locked={!isAuthed}
            onRequestLogin={onRequestLogin}
          />
        </div>

        {/* OBV — volume confirmation / divergence */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 flex flex-col h-full">
          <div className="flex items-start justify-between gap-2 mb-1 min-h-[2.5rem]">
            <span className="flex items-start gap-1"><h4 className="text-sm font-medium leading-tight text-gray-700 dark:text-gray-300">OBV</h4><InfoHint title="OBV — volume flow" align="left">On-balance volume: adds volume on up days, subtracts on down days. If price makes a new low but OBV doesn't, sellers are exhausting (bullish divergence) — and vice versa. The badge shows the current read.</InfoHint></span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded shrink-0 whitespace-nowrap ${divergenceBadge.cls}`}>
              {divergenceBadge.text}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={obvData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="time" tick={false} />
              <YAxis domain={['auto', 'auto']} width={45} tick={{ fontSize: 10 }}
                stroke="#6B7280" tickFormatter={(v) => compactNumber(v)} />
              <Tooltip
                formatter={(v) => [compactNumber(v), 'OBV']}
                labelFormatter={(t) => formatTime(t)}
                contentStyle={tooltipStyle}
              />
              <Line type="monotone" dataKey="obv" stroke="#F59E0B" strokeWidth={2} dot={false} name="OBV" />
            </LineChart>
          </ResponsiveContainer>
          <RuleTag
            name="OBV Divergence"
            description="Buy when price sets a lower low but cumulative volume flow (OBV) sets a higher low — sellers exhausting. Sell on the bearish mirror image."
            stat={evidence?.obv}
            locked={!isAuthed}
            onRequestLogin={onRequestLogin}
          />
        </div>

        {/* DCA vs deposit — a peer of the indicator panes */}
        <DCAPane symbol={symbol} tooltipStyle={tooltipStyle} locked={!isAuthed} onRequestLogin={onRequestLogin} />
      </div>
    </div>
  );
};

// Strategy tag below each pane: name + win rate, with a hover card giving the
// rule description and its full stats on this symbol/timeframe.
const RuleTag = ({ name, description, stat, locked = false, onRequestLogin = () => {} }) => {
  if (!stat) return null;
  // Gated for logged-out visitors: the mini-chart above stays visible, but the
  // historical win-rate stat unlocks on login.
  if (locked) {
    return (
      <div className="mt-auto pt-1 flex justify-center">
        <button
          onClick={() => onRequestLogin('Indicator win-rate backtests on this symbol')}
          className="text-[11px] font-medium text-gray-400 hover:text-emerald-600 cursor-pointer text-center leading-tight border-b border-dotted border-gray-300"
          title="Log in to see this rule's historical win rate on this symbol"
        >
          🔒 {name} · log in for win rate
        </button>
      </div>
    );
  }
  const thin = stat.n < 15;
  const color = thin ? 'text-gray-400'
    : stat.winPct >= 55 ? 'text-green-600'
    : stat.winPct <= 45 ? 'text-red-600'
    : 'text-gray-500';
  return (
    <div className="relative group mt-auto pt-1 flex justify-center">
      <span className={`text-[11px] font-medium cursor-help text-center leading-tight border-b border-dotted border-gray-300 ${color}`}>
        {name} · {stat.winPct}% ({stat.n}) · {stat.avgPct >= 0 ? '+' : ''}{stat.avgPct.toFixed(1)}% · ~{stat.avgBars} bars
      </span>
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-30 w-72 rounded-lg bg-white border border-gray-200 shadow-xl p-3 text-left">
        <div className="text-xs font-semibold text-gray-900 mb-1">{name}</div>
        <div className="text-[11px] text-gray-600 mb-2">{description}</div>
        <div className="text-[11px] text-gray-800 space-y-0.5">
          <div>Win rate: <strong>{stat.winPct}%</strong> over {stat.n} round trips</div>
          <div>Average per trade: <strong>{stat.avgPct >= 0 ? '+' : ''}{stat.avgPct.toFixed(2)}%</strong></div>
          <div>Average hold: <strong>~{stat.avgBars} bars</strong> (bars of this chart timeframe)</div>
        </div>
        <div className="text-[10px] text-gray-400 mt-2">
          Signal-close to signal-close on this symbol and timeframe, no fees —
          measures whether price follows the indicator, not net trading profit.
        </div>
      </div>
    </div>
  );
};

// ---- local calculations ----

function sma(values, period) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

function rsiSeries(closes, period = 14) {
  const out = new Array(closes.length).fill(null);
  let avgGain = 0, avgLoss = 0;
  const a = 1 / period;
  for (let i = 1; i < closes.length; i++) {
    const delta = closes[i] - closes[i - 1];
    avgGain += a * ((delta > 0 ? delta : 0) - avgGain);
    avgLoss += a * ((delta < 0 ? -delta : 0) - avgLoss);
    if (i >= period) {
      out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    }
  }
  return out;
}

function onBalanceVolume(candles) {
  const out = new Array(candles.length).fill(0);
  for (let i = 1; i < candles.length; i++) {
    const vol = Number(candles[i].volume) || 0;
    const diff = candles[i].close - candles[i - 1].close;
    out[i] = out[i - 1] + (diff > 0 ? vol : diff < 0 ? -vol : 0);
  }
  return out;
}

// Trade a buy/sell signal rule through history: enter at the close of a buy
// signal, exit at the close of the next sell signal (open trades at series end
// close at the last bar). Win = exit above entry. Pure price follow-through.
function ruleWinRate(buySignal, sellSignal, closes) {
  let entry = null, entryIdx = null;
  let wins = 0, total = 0, sumRet = 0, sumBars = 0;
  for (let i = 0; i < closes.length; i++) {
    if (entry === null && buySignal[i]) {
      entry = closes[i]; entryIdx = i;
    } else if (entry !== null && sellSignal[i]) {
      const ret = closes[i] / entry - 1;
      total += 1; sumRet += ret; sumBars += i - entryIdx;
      if (ret > 0) wins += 1;
      entry = null;
    }
  }
  if (entry !== null) {
    const ret = closes[closes.length - 1] / entry - 1;
    total += 1; sumRet += ret; sumBars += closes.length - 1 - entryIdx;
    if (ret > 0) wins += 1;
  }
  if (total === 0) return null;
  return {
    winPct: Math.round((wins / total) * 100),
    n: total,
    avgPct: (sumRet / total) * 100,
    avgBars: Math.round(sumBars / total),
  };
}

// All historical divergence events (for evidence stats): every consecutive
// pivot pair where price made a new extreme that OBV refused to confirm.
function allDivergenceEvents(closes, obv, pivotW = 3) {
  const n = closes.length;
  const pivotHighs = [];
  const pivotLows = [];
  for (let i = pivotW; i < n - pivotW; i++) {
    let isHigh = true, isLow = true;
    for (let j = i - pivotW; j <= i + pivotW; j++) {
      if (closes[j] > closes[i]) isHigh = false;
      if (closes[j] < closes[i]) isLow = false;
    }
    if (isHigh) pivotHighs.push(i);
    if (isLow) pivotLows.push(i);
  }
  const events = [];
  for (let k = 1; k < pivotHighs.length; k++) {
    const [a, b] = [pivotHighs[k - 1], pivotHighs[k]];
    if (closes[b] > closes[a] && obv[b] < obv[a]) events.push({ type: 'BEARISH', at: b });
  }
  for (let k = 1; k < pivotLows.length; k++) {
    const [a, b] = [pivotLows[k - 1], pivotLows[k]];
    if (closes[b] < closes[a] && obv[b] > obv[a]) events.push({ type: 'BULLISH', at: b });
  }
  return events;
}

// Compare the last two price pivots against OBV at the same bars: price making a
// new extreme that OBV refuses to confirm is a divergence.
function detectObvDivergence(closes, obv, lookback = 60, pivotW = 3) {
  const n = closes.length;
  const start = Math.max(pivotW, n - lookback);
  const pivotHighs = [];
  const pivotLows = [];

  for (let i = start; i < n - pivotW; i++) {
    let isHigh = true, isLow = true;
    for (let j = i - pivotW; j <= i + pivotW; j++) {
      if (closes[j] > closes[i]) isHigh = false;
      if (closes[j] < closes[i]) isLow = false;
    }
    if (isHigh) pivotHighs.push(i);
    if (isLow) pivotLows.push(i);
  }

  let bearish = null;
  if (pivotHighs.length >= 2) {
    const [a, b] = pivotHighs.slice(-2);
    if (closes[b] > closes[a] && obv[b] < obv[a]) bearish = b;
  }
  let bullish = null;
  if (pivotLows.length >= 2) {
    const [a, b] = pivotLows.slice(-2);
    if (closes[b] < closes[a] && obv[b] > obv[a]) bullish = b;
  }

  if (bearish !== null && (bullish === null || bearish > bullish)) return { type: 'BEARISH', at: bearish };
  if (bullish !== null) return { type: 'BULLISH', at: bullish };
  return { type: 'NONE' };
}

function compactNumber(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toFixed(0);
}

export default CompactMonitoringChart;
